import RelatedEntityRelationship from "../../../../Global/Models/Related/RelatedEntityRelationship";
import EventGamesModel from "../EventGamesModel";
import EventGameTiebreakerModel from "../EventGameTiebreakerModel";
import EventGamePredictionModel from "../Prediction/EventGamePredictionModel";
import BracketGameTiebreaker from "../Prediction/EventGameTiebreaker";
import EventGameRankingsModel from "../Rankings/EventGameRankingsModel";
import EventGameModel from "../EventGameModel";
import EventGameFixturesModel, {
  EventGameOutcomeTypeEnum,
} from "../EventGameFixturesModel";
import EventGameFixtures from "../Prediction/RequestBody/EventGameFixtures";
import EventGamePredictionFixturesModel from "../Prediction/EventGamePredictionFixturesModel";
import EventGamePredictionsModel from "../EventGamePredictionsModel";

export default class EventGameRemapper {
  public remapEventGames = (response: any) => {
    const model = new EventGamesModel();

    model.meta.pagination.itemsPerPage =
      response.meta.pagination.items_per_page;
    model.meta.pagination.nextPageStartsAfter =
      response.meta.pagination.next_page_starts_after;
    model.data = response.data.map((d) => this.remapEventGameById(d));

    return model;
  };

  public remapEventGameById = (response: any) => {
    const model = new EventGameModel();

    model.id = response.id;
    model.title = response.title;
    model.description = response.description;
    model.rules = response.rules;
    model.customFields = response.custom_fields;
    model.images = response.images;
    model.type = response.type;
    model.status = response.status;
    model.related = response.related
      ? this.remapRelated(response.related)
      : response.related;
    model.tiebreaker = response.tiebreaker
      ? this.remapEventGameTiebreaker(response.tiebreaker)
      : response.tiebreaker;
    model.predictionsCutoff = response.predictions_cutoff;
    model.fixtures = response.fixtures
      ? this.remapEventGameFixtures(response.fixtures)
      : response.fixtures;
    model.createdAt = response.created_at;
    model.updatedAt = response.updated_at;

    return model;
  };

  public remapPredictionToRequestBody = (prediction: EventGameFixtures[]) => {
    return prediction.map((p) => {
      return {
        id: p.id,
        prediction_type: p.predictionType,
        prediction: p.prediction,
      };
    });
  };

  public remapPrediction = (response: any) => {
    const model = new EventGamePredictionModel();

    model.id = response.id;
    model.profileId = response.profile_id;
    model.gameId = response.game_id;
    model.totalPoints = response.total_points;
    model.gamePoints = response.game_points;
    model.bonusPoints = response.bonus_points;
    model.position = response.position;
    model.status = response.status;
    model.fixtures = this.remapPredictionFixtures(response.fixtures);
    model.tiebreaker = response.tiebreaker
      ? this.remapPredictionTiebreaker(response.tiebreaker)
      : response.tiebreaker;
    model.createdAt = response.created_at;
    model.updatedAt = response.updated_at;

    return model;
  };

  public remapPredictionsForUser = (response: any) => {
    const model = new EventGamePredictionsModel();

    model.meta.pagination.itemsPerPage =
      response.meta.pagination.items_per_page;
    model.meta.pagination.nextPageStartsAfter =
      response.meta.pagination.next_page_starts_after;
    model.data = response.data.map((d) => this.remapPrediction(d));

    return model;
  };

  public remapRankings = (response: any) => {
    const model = new EventGameRankingsModel();

    model.meta.pagination.itemsPerPage =
      response.meta.pagination.items_per_page;
    model.meta.pagination.nextPageStartsAfter =
      response.meta.pagination.next_page_starts_after;
    model.data = response.data.map((d) => this.remapPrediction(d));

    return model;
  };

  private remapRelated = (response: any[]) => {
    return response.map((r) => {
      const model = new RelatedEntityRelationship();

      model.entityId = r.entity_id;
      model.entityType = r.entity_type;
      model.entityRelationship = r.entity_relationship;

      return model;
    });
  };

  private remapPredictionFixtures = (response: any) => {
    return response.map((d) => {
      let model = new EventGamePredictionFixturesModel();

      switch (d.prediction_type) {
        case EventGameOutcomeTypeEnum.BOOLEAN:
          model =
            new EventGamePredictionFixturesModel<EventGameOutcomeTypeEnum.BOOLEAN>();
          break;
        case EventGameOutcomeTypeEnum.NUMBER:
          model =
            new EventGamePredictionFixturesModel<EventGameOutcomeTypeEnum.NUMBER>();
          break;
        case EventGameOutcomeTypeEnum.ENUM:
          model =
            new EventGamePredictionFixturesModel<EventGameOutcomeTypeEnum.ENUM>();
          break;
        case EventGameOutcomeTypeEnum.FREE_INPUT:
          model =
            new EventGamePredictionFixturesModel<EventGameOutcomeTypeEnum.FREE_INPUT>();
          break;
      }

      model.id = d.id;
      model.predictionType = d.prediction_type;
      model.prediction = d.prediction;
      model.points = d.points;
      model.correct = d.correct;

      return model;
    });
  };

  private remapPredictionTiebreaker = (response: any) => {
    const model = new BracketGameTiebreaker();

    model.statTotal = response.stat_total;

    return model;
  };

  private remapEventGameFixtures = (response: any) => {
    return response.map((f) => {
      let model = new EventGameFixturesModel();

      switch (f.outcome_type) {
        case EventGameOutcomeTypeEnum.BOOLEAN:
          model =
            new EventGameFixturesModel<EventGameOutcomeTypeEnum.BOOLEAN>();
          break;
        case EventGameOutcomeTypeEnum.NUMBER:
          model = new EventGameFixturesModel<EventGameOutcomeTypeEnum.NUMBER>();
          break;
        case EventGameOutcomeTypeEnum.ENUM:
          model = new EventGameFixturesModel<EventGameOutcomeTypeEnum.ENUM>();
          break;
        case EventGameOutcomeTypeEnum.FREE_INPUT:
          model =
            new EventGameFixturesModel<EventGameOutcomeTypeEnum.FREE_INPUT>();
          break;
      }

      model.id = f.id;
      model.question = f.question;
      model.status = f.status;
      model.voidReason = f.void_reason;
      model.points = f.points;
      model.outcomeType = f.outcome_type;
      model.validOutcomes = f.valid_outcomes;
      model.outcome = f.outcome;

      return model;
    });
  };

  private remapEventGameTiebreaker = (response: any) => {
    const model = new EventGameTiebreakerModel();

    model.statTiebreakerEnabled = response.stat_tiebreaker_enabled;
    model.statTotal = response.stat_total;

    return model;
  };
}
